import React, { Fragment, useEffect } from "react";
import { Helmet } from "react-helmet";
import gql from "graphql-tag";
import styled from "@emotion/styled";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { LoadingPage } from "../UI";
import { FlexMaxWidth } from "../UI/Containers";
import PageHeader from "../Page/PageHeader";
import { prefixImageUrl } from "../utils/imageUrl";

const PageSection = styled("section")`
  line-height: 1.5em;
  &:nth-child(even) {
    background: ${(props) => props.theme.colors.accent};
  }
  padding: ${(props) => props.theme.spacing.default};
  ${(props) => props.theme.bp.below.sm} {
    padding: ${(props) => props.theme.spacing.small};
  }
  .image-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    img {
      max-width: 90%;
      width: auto;
      height: auto;
      margin-top: 1rem;
    }
  }

  /* Styles for the dangerouslySetInnerHTML content */
  .description-content {
    flex: 2; /* Allow content to take more space */
    padding-right: 2rem; /* Add space between content and sidebar */
    ${(props) => props.theme.bp.below.sm} {
        padding-right: 0;
        margin-bottom: 2rem; /* Add space below content on small screens */
    }

    p {
      margin-bottom: 1em; /* Add space below paragraphs */
      line-height: 1.6; /* Slightly increase line height for readability */
    }
    
    ul {
      list-style: disc; /* Ensure bullets are visible */
      margin-left: 1.5em; /* Indent list */
      margin-bottom: 1em;
      padding-left: 0;
    }

    li {
      margin-bottom: 0.5em; /* Space out list items */
    }

    /* Add more specific styles if needed, e.g., for h1, h2, strong */
  }
`;

const Sidebar = styled("aside")`
  max-width: 16rem;
  width: 100%;
  margin-left: 2rem;
  flex-grow: 1;
  ${(props) => props.theme.bp.below.sm} {
    max-width: 100%;
    margin-left: 0;
  }
  h2 {
    margin-bottom: 0.5rem;
  }
  a,
  span {
    display: block;
  }
  img {
    margin: 1.5rem 0;
    max-width: 100%;
    height: auto;
    max-height: 120px;
    max-width: 120px;
  }
`;

const ContactPerson = (props) => (
  <Fragment>
    <h2>{props.name}</h2>

    {props.email && <a href={"mailto:" + props.email}>{props.email}</a>}
    {props.phone && <span>Telefon: {props.phone}</span>}
    {props.mobile && <span>Mobil: {props.mobile}</span>}
  </Fragment>
);

const SideCol = ({ property }) => (
  <Sidebar>
    {property.contact && <ContactPerson {...property.contact} />}
  </Sidebar>
);

const PageContent = (props) => {
  const rentable = props.data?.rentable;

  useEffect(() => {
    const categoryClass = rentable?.property?.category?.toLowerCase() || 'default';
    document.body.className = categoryClass;
    return () => {
      document.body.className = 'default';
    };
  }, [rentable?.property?.category]);

  if (!rentable) {
    return null;
  }

  const property = rentable.property;

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{property ? property.title : "Ledigt Objekt"}</title>
      </Helmet>
      {property?.image?.url ? (
        <PageHeader
          pageData={{
            ...property,
            image: {
              ...property.image,
              url: prefixImageUrl(property.image.url),
            },
            excerpt: `${rentable.type} ${
              rentable.space > 0 ? rentable.space + "m²" : ""
            }`,
          }}
        />
      ) : null}
      <React.Fragment>
        <PageSection>
          <FlexMaxWidth>
            <div
              className="description-content" 
              dangerouslySetInnerHTML={{
                __html: rentable.content,
              }}
            />
            <SideCol property={rentable} />
          </FlexMaxWidth>
          <FlexMaxWidth>
            <div className="image-container">
              {rentable.images.length > 0 &&
                rentable.images.map(({ image }, index) => {
                  if (!image) return null;
                  return (
                    <img
                      key={index}
                      src={prefixImageUrl(image.url)}
                      width={image.width}
                      height={image.height}
                      alt=""
                    />
                  );
                })}
            </div>
          </FlexMaxWidth>
        </PageSection>
      </React.Fragment>
    </div>
  );
};

const RentPage = () => {
  const { slug } = useParams();

  const { loading, error, data } = useQuery(query, {
    variables: { slug },
  });

  if (loading) return <LoadingPage />;
  if (error) {
    console.error("Error fetching rentable:", error);
    return <p>Error loading page.</p>;
  }

  return <PageContent data={data} />;
}

const query = gql`
  query Property($slug: String) {
    rentable(slug: $slug) {
      space
      content
      type
      contact {
        name
        email
        phone
        mobile
      }
      images {
        image {
          url
          width
          height
        }
      }
      property {
        title
        content
        excerpt
        website
        category
        image {
          url
          width
          height
        }
        logo {
          url
        }
      }
    }
  }
`;

export default RentPage;
